import { Truck, ShieldCheck, RotateCcw, Leaf } from "lucide-react";
import { motion } from "framer-motion";
import { REVIEWS, RESULTS, TRUST_ICONS } from "@/lib/content";
import { useI18n } from "@/lib/i18n";
import { Stars } from "./ProductCard";
import { useReviews } from "@/lib/reviews";
import { Link } from "react-router-dom";
import { useProducts, cleanTitle } from "@/lib/api";
import { Reveal, SplitText, CountUp } from "@/components/fx/Motion";

const ICONS = { Truck, ShieldCheck, RotateCcw, Leaf };

export const TrustBar = ({ compact }) => {
  const { t } = useI18n();
  return (
    <div data-testid="trust-bar" className={`grid grid-cols-2 ${compact ? "gap-4" : "lg:grid-cols-4 gap-6 lg:gap-10"}`}>
      {TRUST_ICONS.map((name, i) => {
        const I = ICONS[name];
        return (
          <Reveal key={name} delay={i * 0.1} y={20} className="flex items-start gap-3 group">
            <I size={compact ? 18 : 22} className="text-gold shrink-0 mt-0.5 transition-transform duration-500 group-hover:-translate-y-1 group-hover:scale-110" strokeWidth={1.5} />
            <div>
              <p className={`${compact ? "text-xs" : "text-sm"} font-semibold`}>{t(`trust_${i + 1}_title`)}</p>
              <p className="text-xs text-white/50">{t(`trust_${i + 1}_text`)}</p>
            </div>
          </Reveal>
        );
      })}
    </div>
  );
};

export const Marquee = () => {
  const { t } = useI18n();
  const items = [1, 2, 3, 4, 5, 6].map((i) => t(`marquee_${i}`));
  return (
    <div data-testid="brand-marquee" className="border-y border-white/5 py-6 overflow-hidden bg-[#0e0d0b] -rotate-1 scale-[1.02] my-4">
      <div className="marquee">
        {[...items, ...items, ...items, ...items].map((b, i) => (
          <span key={i} className="font-display italic text-2xl lg:text-4xl text-white/55 px-8 flex items-center gap-8 whitespace-nowrap hover:text-[#e3cb94] transition-colors">
            {b}<span className="text-gold text-sm not-italic">◆</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export const Results = () => {
  const { t } = useI18n();
  return (
    <section data-testid="results-section" className="max-w-7xl mx-auto px-5 lg:px-10 py-28">
      <p className="eyebrow mb-4">{t("results_eyebrow")}</p>
      <h2 className="font-display text-3xl lg:text-5xl mb-14 max-w-2xl"><SplitText text={t("results_title")} /></h2>
      <div className="grid md:grid-cols-3 gap-10">
        {RESULTS.map((v, i) => (
          <Reveal key={v} delay={i * 0.15} className="relative pt-6">
            <motion.div className="absolute top-0 left-0 h-px bg-[#c9a55c]/60" initial={{ width: 0 }} whileInView={{ width: "100%" }} viewport={{ once: true }} transition={{ duration: 1.4, delay: i * 0.15 }} />
            <p data-testid={`result-value-${i}`} className="font-display text-6xl lg:text-8xl text-gold"><CountUp to={v} suffix="%" /></p>
            <p className="text-sm text-white/60 mt-3 max-w-xs">{t(`result_${i}`)}</p>
          </Reveal>
        ))}
      </div>
      <p className="text-[11px] text-white/30 mt-8">{t("results_note")}</p>
    </section>
  );
};

const ReviewProduct = ({ handle, fallback, products }) => {
  const { t } = useI18n();
  const p = products.find((x) => x.handle === handle);
  if (!p) return <span className="text-gold">{t("verified")} · {fallback}</span>;
  return (
    <Link data-testid={`review-product-${handle}`} to={`/products/${handle}`} className="mt-4 flex items-center gap-3 border-t border-white/10 pt-4 group/p">
      <img src={p.images[0]} alt="" className="h-12 w-12 object-contain bg-white shrink-0" />
      <span className="min-w-0">
        <span className="block text-[10px] uppercase tracking-[0.15em] text-white/40">{t("verified")} · {t("review_about")}</span>
        <span className="block text-sm text-gold line-clamp-2 group-hover/p:underline">{cleanTitle(p.title)}</span>
      </span>
    </Link>
  );
};

export const Reviews = ({ pdp }) => {
  const { t } = useI18n();
  const { data: real = [] } = useReviews();
  const { data: products = [] } = useProducts({});
  const cards = [
    ...real.map((r) => ({ key: r.id, name: r.name, city: "", rating: r.rating, product: r.product_title, handle: r.product_handle, text: r.text })),
    ...REVIEWS.map((r, i) => ({ ...r, key: r.name, text: t(`review_${i}_${r.g}`) })),
  ];
  return (
    <section data-testid="reviews-section" className="py-24 bg-[#0e0d0b] border-y border-white/5 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div>
            <p className="eyebrow mb-4">{t("reviews_eyebrow")}</p>
            <h2 className="font-display text-3xl lg:text-5xl"><SplitText text={t(pdp ? "reviews_title_pdp" : "reviews_title")} /></h2>
          </div>
          <div className="flex items-center gap-3"><Stars size={18} /><span className="text-sm text-white/60">{t("avg_rating")}</span></div>
        </div>
        <div className="flex gap-5 overflow-x-auto no-scrollbar snap-x pb-2" data-lenis-prevent-horizontal>
          {cards.map((r, i) => (
            <motion.figure key={r.key} data-testid="review-card" initial={{ opacity: 0, x: 80 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.9, delay: Math.min(i, 5) * 0.1, ease: [0.22, 1, 0.36, 1] }} whileHover={{ y: -8 }}
              className="snap-start shrink-0 w-[85%] sm:w-[360px] border border-white/10 p-7 bg-[#0b0a08] hover:border-[#c9a55c]/50 transition-colors flex flex-col">
              <Stars value={r.rating} />
              <blockquote className="font-display text-lg leading-relaxed mt-4 mb-6 flex-1">“{r.text}”</blockquote>
              <figcaption className="text-xs text-white/50"><span className="text-white">{r.name}</span>{r.city && ` · ${r.city}`}
                <ReviewProduct handle={r.handle} fallback={r.product} products={products} />
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};
